import { GetServiceV2Dto, GetTransactionDto } from "@api/api";
import { ServiceEnum } from "@core/model/service-enum";
import { useDateTimeContext } from "@shared/utility/date-time-service/date-time.context";
import { add, intervalToDuration, parse } from "date-fns";
import { useCallback } from "react";
import { BookingEnum } from "@core/model/booking-enum";
import { ExtendedService } from "@core/model/extended-service.interface";

export function useTransactionUtil() {
  const { dateFormat, convertToISO8601 } = useDateTimeContext();
  const getServiceStart = (service: GetServiceV2Dto) => {
    const startDate = service?.date;
    const startTime = service?.time;

    if (startDate && startTime) {
      return parse(
        `${startDate} ${startTime}`,
        `${dateFormat} HH:mm`,
        new Date()
      );
    }

    const createdAt = service?.created_at;
    if (createdAt) {
      try {
        const isoString = convertToISO8601(createdAt);
        return new Date(isoString);
      } catch {
        return new Date(createdAt);
      }
    }
    return new Date();
  };

  const getEndDateTime = (service: GetServiceV2Dto) => {
    const duration = service?.duration;
    let startDateTime = getServiceStart(service);

    const expiryDate = add(startDateTime, {
      minutes: duration,
    });

    return expiryDate;
  };

  function convertMinutesToHours(minutes: number): string {
    const duration = intervalToDuration({ start: 0, end: minutes * 60 * 1000 });

    const hours = duration.hours ?? 0;
    const remainingMinutes = duration.minutes ?? 0;

    return `${hours}.${remainingMinutes}`;
  }

  const getTimerClassName = useCallback((notice: string) => {
    if (notice === ServiceEnum.Checkout)
      return "bg-green-100 border-2 border-green-500";
    if (notice === "N/A" || notice === "Calculating")
      return "bg-blue-100 border-2 border-blue-500";
    if (notice.includes("s left")) {
      // If there are hours left, it is not critical/warning
      if (notice.includes("hr")) {
        return "bg-blue-100 border-2 border-blue-500";
      }

      // Check if there are minutes left
      if (notice.includes("m")) {
        const minutes = parseInt(notice.split("m")[0], 10);
        if (minutes <= 5) {
          // Less than 5 minutes - warning
          return "bg-orange-200 border-2 border-orange-500 animate-pulse text-orange-800";
        }
      } else {
        // Less than 1 minute (only seconds remaining) - critical
        return "bg-red-200 border-2 border-red-600 animate-pulse text-red-800";
      }
    }
    if (notice.includes("since")) {
      return "bg-green-100 border-2 border-green-500";
    }
    // Otherwise, it is an expired session showing total consumed duration
    return "bg-red-100 border-2 border-red-500 animate-pulse";
  }, []);

  const sortServicesByExpiration = useCallback((services: ExtendedService[]) => {
    return [...services].sort((a, b) => {
      const isOngoingA = a.status === BookingEnum.Ongoing;
      const isOngoingB = b.status === BookingEnum.Ongoing;

      if (isOngoingA && !isOngoingB) return -1;
      if (!isOngoingA && isOngoingB) return 1;

      if (isOngoingA && isOngoingB) {
        const isOpenA = !!a.is_open_time;
        const isOpenB = !!b.is_open_time;

        if (!isOpenA && isOpenB) return -1;
        if (isOpenA && !isOpenB) return 1;

        if (!isOpenA && !isOpenB) {
          const endA = getEndDateTime(a).getTime();
          const endB = getEndDateTime(b).getTime();
          return endA - endB;
        }

        const startA = getServiceStart(a).getTime();
        const startB = getServiceStart(b).getTime();
        return startB - startA;
      }

      const startA = getServiceStart(a).getTime();
      const startB = getServiceStart(b).getTime();
      return startB - startA;
    });
  }, [getServiceStart, getEndDateTime]);

  const getTransactionSortValue = useCallback((t: GetTransactionDto) => {
    const isOngoing = t.status === BookingEnum.Ongoing;
    
    if (isOngoing) {
      const ongoingServices = (t.service || []).filter(
        (s) => !s.is_checkout && !s.is_open_time
      );
      if (ongoingServices.length > 0) {
        const endTimes = ongoingServices.map((s) => getEndDateTime(s).getTime());
        return { isOngoing: true, isOpen: false, time: Math.min(...endTimes) };
      }
      
      const openServices = (t.service || []).filter((s) => !s.is_checkout);
      if (openServices.length > 0) {
        const startTimes = openServices.map((s) => getServiceStart(s).getTime());
        return { isOngoing: true, isOpen: true, time: Math.max(...startTimes) };
      }
    }

    const services = t.service || [];
    if (services.length > 0) {
      const startTimes = services.map((s) => getServiceStart(s).getTime());
      return { isOngoing: false, isOpen: false, time: Math.max(...startTimes) };
    }

    const updatedTime = t.updated_at ? new Date(t.updated_at).getTime() : 0;
    return { isOngoing: false, isOpen: false, time: updatedTime };
  }, [getServiceStart, getEndDateTime]);

  const sortTransactionsByExpiration = useCallback((transactions: GetTransactionDto[]) => {
    return [...transactions].sort((a, b) => {
      const sortA = getTransactionSortValue(a);
      const sortB = getTransactionSortValue(b);

      if (sortA.isOngoing && !sortB.isOngoing) return -1;
      if (!sortA.isOngoing && sortB.isOngoing) return 1;

      if (sortA.isOngoing && sortB.isOngoing) {
        if (!sortA.isOpen && sortB.isOpen) return -1;
        if (sortA.isOpen && !sortB.isOpen) return 1;

        if (!sortA.isOpen && !sortB.isOpen) {
          return sortA.time - sortB.time;
        }

        return sortB.time - sortA.time;
      }

      return sortB.time - sortA.time;
    });
  }, [getTransactionSortValue]);

  return {
    getServiceStart,
    getEndDateTime,
    convertMinutesToHours,
    getTimerClassName,
    sortServicesByExpiration,
    sortTransactionsByExpiration
  };
}
